/**
 * The landholder register (Diurnal parity B4: 05-017, 04-026, 05-018; docs/specs/map.md §28).
 *
 * MB's decision 618 (Q543 A): landholder parcels are a record shared by every laptop and Teslyn Cloud (`land_parcel`,
 * Field 0050, Postgres 20261027006000); field crew, managers and data processors edit notes and flags, office staff
 * read. Decision 83 (Q56): contacts, notes and flags are internal only, and only managers may export contacts. The
 * list, folders, search, concerns, Combine and the KML export are the Diurnal tool's (`aircraft-map.js` 23830-25278:
 * `pinProperty`, `combineSelectedPinned`, `exportPinnedKML`, `_geojsonToKmlGeometry`, `renderPinnedPropertiesList`,
 * folders and search; `showPinnedExportMenu` 26752). Shown or hidden, the fill opacity and folders not used yet are
 * each laptop's own choice (the map's preferences).
 *
 * What differs from the tool, on purpose: Combine keeps each property's notes, flags and contacts (the tool dropped
 * them); notes of several lines are written as one `Notes:` line each, so they come back whole; a manager's export
 * carries the contacts and attributes as ExtendedData (decision 83), anyone else's none; exporting folders leaves the
 * person's selection as it was (the tool cleared it, inventory 05 finding: AM:26812).
 */
import { canExportLandholderContacts, type TenantRole } from './hazards.js';
import type { KmlGeometry, LandParcel } from './map-kml.js';
import { hasConcern } from './map-kml.js';
import { PROPERTY_PALETTE, type PropertyColour } from './map-layers.js';
import { combinedGeometry, combinedName, geometryKey } from './property-layers.js';

// ------------------------------------------------------------------------------------------------ who may change

/** Who edits the register (decisions 618, 790): field crew, managers, data processors and tenant admins. */
export const LANDHOLDER_EDIT_ROLES = ['tenant_admin', 'manager', 'field_crew', 'data_processor'] as const;

/**
 * May this person change the register: pin, import, rename, move, edit notes and flags, combine, remove (decision
 * 618)? Null roles (not known on this laptop yet): yes, and Teslyn Cloud decides when the change syncs.
 */
export function canEditLandholders(roles: readonly string[] | null): boolean {
  if (roles === null) return true;
  return roles.some((r) => (LANDHOLDER_EDIT_ROLES as readonly string[]).includes(r));
}

/** The words beside the list for someone who reads only. */
export const LANDHOLDERS_READ_ONLY =
  'You can read the landholder boundaries. Field crew, managers and data processors change them, their notes and their flags.';

// ------------------------------------------------------------------------------------------------ the record

/** A `land_parcel` row as Teslyn Field's core gives it (`teslyn_db::LandParcel`). */
export interface LandParcelRecord {
  readonly id: string;
  readonly project_id: string;
  readonly name: string;
  readonly source: string;
  readonly folder: string;
  readonly geometry: KmlGeometry;
  readonly notes: string;
  readonly needs_notification: boolean;
  readonly objection: boolean;
  readonly contacts: readonly string[];
  readonly attributes: ReadonlyArray<readonly [string, string]>;
  readonly created_at: string;
  readonly updated_at: string;
  readonly updated_by: string | null;
  readonly deleted_at: string | null;
}

/** The parcels of the records, shown unless hidden on this laptop. */
export function parcelsOfRecords(
  records: readonly LandParcelRecord[],
  hidden: ReadonlySet<string>,
): LandParcel[] {
  return records
    .filter((r) => r.deleted_at === null)
    .map((r) => ({
      id: r.id,
      name: r.name,
      source: r.source,
      folder: r.folder,
      geometry: r.geometry,
      notes: r.notes,
      needs_notification: r.needs_notification,
      objection: r.objection,
      contacts: Array.isArray(r.contacts) ? r.contacts : [],
      attributes: Array.isArray(r.attributes) ? r.attributes : [],
      visible: !hidden.has(r.id),
    }));
}

/** A parcel to store: the record's columns (no id for a new one). */
export interface LandParcelSave {
  readonly id?: string;
  readonly name: string;
  readonly source: string;
  readonly folder: string;
  readonly geometry: KmlGeometry;
  readonly notes: string;
  readonly needs_notification: boolean;
  readonly objection: boolean;
  readonly contacts: readonly string[];
  readonly attributes: ReadonlyArray<readonly [string, string]>;
}

/** A parcel as it is stored: new ones (an id that is not a record's yet, `parcel-…`) without an id. */
export function saveOfParcel(p: LandParcel, isRecord: (id: string) => boolean): LandParcelSave {
  return {
    ...(isRecord(p.id) ? { id: p.id } : {}),
    name: p.name,
    source: p.source,
    folder: p.folder,
    geometry: p.geometry,
    notes: p.notes,
    needs_notification: p.needs_notification,
    objection: p.objection,
    contacts: p.contacts,
    attributes: p.attributes,
  };
}

/** The fill opacity of the boundaries when nobody changed it (am:844): 15 %. */
export const DEFAULT_LANDHOLDER_FILL = 0.15;

// ------------------------------------------------------------------------------------------------ the list

/** Does a parcel match the search (am:24849): its name, source, notes, contacts or any attribute. */
export function parcelMatches(p: LandParcel, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const text = [p.name, p.source, p.notes, ...p.contacts, ...p.attributes.map(([, v]) => v)]
    .join(' ')
    .toLowerCase();
  return text.includes(q);
}

const byName = (a: LandParcel, b: LandParcel): number =>
  a.name.localeCompare(b.name, 'en-AU', { sensitivity: 'base' });

export interface LandholderFolderView {
  readonly name: string;
  readonly items: readonly LandParcel[];
  /** How many it holds, whatever the search. */
  readonly count: number;
  readonly allVisible: boolean;
  readonly noneVisible: boolean;
}

export interface LandholderListView {
  readonly count: number;
  /** Unfiled parcels, A to Z. */
  readonly unfiled: readonly LandParcel[];
  /** Every folder, A to Z: those the person made and those the parcels are in; a folder with no match hidden while searching. */
  readonly folders: readonly LandholderFolderView[];
  readonly allVisible: boolean;
  readonly noneVisible: boolean;
  /** Parcels with an objection, a notification needed or notes. */
  readonly concerns: number;
  readonly concernsAllVisible: boolean;
  readonly concernsNoneVisible: boolean;
}

/** The list as the tool shows it (`renderPinnedPropertiesList`, am:24910): unfiled first, then each folder, A to Z. */
export function landholderList(
  parcels: readonly LandParcel[],
  folders: readonly string[],
  query = '',
): LandholderListView {
  const shown = parcels.filter((p) => parcelMatches(p, query));
  const searching = query.trim() !== '';
  const names = [...new Set([...folders, ...parcels.map((p) => p.folder).filter(Boolean)])].sort((a, b) =>
    a.localeCompare(b, 'en-AU', { sensitivity: 'base' }),
  );
  const concerns = parcels.filter(hasConcern);
  return {
    count: parcels.length,
    unfiled: shown.filter((p) => !p.folder).sort(byName),
    folders: names
      .map((name) => {
        const all = parcels.filter((p) => p.folder === name);
        return {
          name,
          items: shown.filter((p) => p.folder === name).sort(byName),
          count: all.length,
          allVisible: all.length > 0 && all.every((p) => p.visible),
          noneVisible: all.length > 0 && all.every((p) => !p.visible),
        };
      })
      .filter((f) => !searching || f.items.length > 0),
    allVisible: parcels.length > 0 && parcels.every((p) => p.visible),
    noneVisible: parcels.every((p) => !p.visible),
    concerns: concerns.length,
    concernsAllVisible: concerns.length > 0 && concerns.every((p) => p.visible),
    concernsNoneVisible: concerns.every((p) => !p.visible),
  };
}

/** The notes under a parcel's name: the first 70 characters (am:24832). */
export const notesPreview = (notes: string): string => (notes.length > 70 ? `${notes.slice(0, 70)}…` : notes);

// ------------------------------------------------------------------------------------------------ changes

/** Why a folder name cannot be used (a new folder or a rename), or null (am:24699, 24715). */
export function folderNameProblem(name: string, existing: readonly string[], was?: string): string | null {
  const n = name.trim();
  if (!n) return 'Type a name for the folder';
  if (n.length > 200) return 'Keep the folder name to 200 characters';
  if (n !== was && existing.includes(n)) return `Folder "${n}" already exists`;
  return null;
}

/** Why a parcel's name cannot be used, or null. */
export function parcelNameProblem(name: string): string | null {
  const n = name.trim();
  if (!n) return 'Type a name for the landholder boundary';
  if (n.length > 200) return 'Keep the name to 200 characters';
  return null;
}

/** The parcels a folder rename changes (am:24715). */
export function renameFolderParcels(parcels: readonly LandParcel[], from: string, to: string): LandParcel[] {
  return parcels.filter((p) => p.folder === from).map((p) => ({ ...p, folder: to.trim() }));
}

/** The parcels a removed folder leaves unfiled (am:24770). */
export function unfileFolderParcels(parcels: readonly LandParcel[], name: string): LandParcel[] {
  return parcels.filter((p) => p.folder === name).map((p) => ({ ...p, folder: '' }));
}

/**
 * Combine the selected parcels into one (`combineSelectedPinned`, am:24200): every polygon of each a part of it,
 * named "A + B", the sources joined, in the first one's folder; the originals go. Notes, flags and contacts are kept
 * (the tool dropped them).
 */
export function combineParcels(selected: readonly LandParcel[]):
  | {
      readonly ok: true;
      readonly parcel: LandParcel;
      readonly remove: readonly string[];
      readonly message: string;
    }
  | { readonly ok: false; readonly message: string } {
  if (selected.length < 2) return { ok: false, message: 'Select 2 or more landholder boundaries to combine' };
  const geometry = combinedGeometry(selected.map((p) => p.geometry));
  if (!geometry) return { ok: false, message: 'No polygon geometry to combine' };
  const name = combinedName(selected.map((p) => p.name));
  const uniq = <T>(xs: readonly T[]): T[] => [...new Set(xs)];
  const parcel: LandParcel = {
    id: '',
    name,
    source: uniq(selected.map((p) => p.source)).join(', '),
    folder: selected[0]!.folder,
    geometry,
    notes: uniq(selected.map((p) => p.notes.trim()).filter(Boolean)).join('\n'),
    needs_notification: selected.some((p) => p.needs_notification),
    objection: selected.some((p) => p.objection),
    contacts: uniq(selected.flatMap((p) => p.contacts)),
    attributes: selected.flatMap((p) => p.attributes),
    visible: true,
  };
  return {
    ok: true,
    parcel,
    remove: selected.map((p) => p.id),
    message: `Combined ${selected.length} landholder boundaries into "${name}"`,
  };
}

// ------------------------------------------------------------------------------------------------ the KML export

/** The 12 fill colours as written into a KML file (`tokens.json` `map-property-1` to `12`; a test keeps them equal). */
export const PROPERTY_PALETTE_HEX: Readonly<Record<string, string>> = {
  'map-property-1': '#00ffff',
  'map-property-2': '#ff6b6b',
  'map-property-3': '#808000',
  'map-property-4': '#6bcf7f',
  'map-property-5': '#b47aff',
  'map-property-6': '#ff9f43',
  'map-property-7': '#54a0ff',
  'map-property-8': '#682068',
  'map-property-9': '#c8ff3e',
  'map-property-10': '#00e5b0',
  'map-property-11': '#ff44dd',
  'map-property-12': '#5566ff',
};

/** The tool's file names (am:24310): the selected properties, or all of them. */
export const landholderExportName = (selected: boolean): string =>
  selected ? 'pinned_properties_selected.kml' : 'pinned_properties_all.kml';

/** The tool's `escapeHtml` (am:25950): `&`, `<`, `>` and `"`. */
const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const coords = (ring: readonly { readonly lat: number; readonly lng: number }[]): string =>
  ring.map((c) => `${c.lng},${c.lat},0`).join(' ');

/** One polygon (`ringsToPolygonKml`, am:24654): outer ring, then each hole. */
function polygonKml(p: Extract<KmlGeometry, { type: 'polygon' }>, indent: string): string {
  let s = `${indent}<Polygon><outerBoundaryIs><LinearRing><coordinates>\n`;
  s += `${indent}  ${coords(p.outer)}\n`;
  s += `${indent}</coordinates></LinearRing></outerBoundaryIs>`;
  for (const h of p.holes) {
    s += `<innerBoundaryIs><LinearRing><coordinates>\n`;
    s += `${indent}  ${coords(h)}\n`;
    s += `${indent}</coordinates></LinearRing></innerBoundaryIs>`;
  }
  return `${s}</Polygon>\n`;
}

function partKml(g: KmlGeometry, indent: string): string {
  switch (g.type) {
    case 'point':
      return `${indent}<Point><coordinates>${g.at.lng},${g.at.lat},0</coordinates></Point>\n`;
    case 'line_string':
      return `${indent}<LineString><coordinates>${coords(g.points)}</coordinates></LineString>\n`;
    case 'polygon':
      return polygonKml(g, indent);
    case 'multi':
      return g.parts.map((x) => partKml(x, indent)).join('');
  }
}

/** A shape as KML (`_geojsonToKmlGeometry`, am:24650): holes kept, so a lease with excluded blocks comes back whole. */
export function geometryKml(g: KmlGeometry): string {
  if (g.type === 'multi') return `    <MultiGeometry>\n${partKml(g, '      ')}    </MultiGeometry>\n`;
  return partKml(g, '    ');
}

/** The description of an exported property (am:24264): its source, then a `Notes:` line per line of notes, then flags. */
export function exportDescription(p: LandParcel): string {
  const parts = [p.source || ''];
  for (const line of p.notes.split(/\r?\n/)) if (line.trim()) parts.push(`Notes: ${line}`);
  const flags = [p.needs_notification ? 'Needs Notification' : '', p.objection ? 'Objection' : ''].filter(
    Boolean,
  );
  if (flags.length > 0) parts.push(`Flags: ${flags.join(', ')}`);
  return parts.join('\n');
}

/** A KML colour (`aabbggrr`) of a `#rrggbb` (am:24260). */
const kmlColour = (hex: string, alpha: string): string => {
  const h = hex.replace('#', '');
  return `${alpha}${h.slice(4, 6)}${h.slice(2, 4)}${h.slice(0, 2)}`;
};

/** The contacts and attributes as ExtendedData, for a manager's export only (decision 83). */
function extendedData(p: LandParcel, indent: string): string {
  const rows: Array<[string, string]> = [
    ...p.contacts.map((c): [string, string] => {
      const i = c.indexOf(': ');
      return i > 0 ? [c.slice(0, i), c.slice(i + 2)] : ['CONTACT', c];
    }),
    ...p.attributes.map(([k, v]): [string, string] => [k, v]),
  ];
  if (rows.length === 0) return '';
  const data = rows
    .map(([k, v]) => `${indent}    <Data name="${esc(k)}"><value>${esc(v)}</value></Data>\n`)
    .join('');
  return `${indent}  <ExtendedData>\n${data}${indent}  </ExtendedData>\n`;
}

function placemarkKml(p: LandParcel, colour: PropertyColour, withContacts: boolean, indent: string): string {
  const hex = PROPERTY_PALETTE_HEX[colour] ?? PROPERTY_PALETTE_HEX['map-property-1']!;
  const line = kmlColour(hex, 'cc');
  let s = `${indent}<Placemark>\n`;
  s += `${indent}  <name>${esc(p.name)}</name>\n`;
  s += `${indent}  <description>${esc(exportDescription(p))}</description>\n`;
  s += `${indent}  <Style>\n`;
  s += `${indent}    <LineStyle><color>${line}</color><width>3</width></LineStyle>\n`;
  s += `${indent}    <PolyStyle><color>44${line.slice(2)}</color></PolyStyle>\n`;
  s += `${indent}  </Style>\n`;
  if (withContacts) s += extendedData(p, indent);
  s += geometryKml(p.geometry);
  s += `${indent}</Placemark>\n`;
  return s;
}

/**
 * The landholder KML (`exportPinnedKML`, am:24252): "Pinned Properties", unfiled properties first, then a Folder per
 * folder; each with its name, a description of its source with `Notes:` and `Flags:` lines (read back on import),
 * its fill colour and its shape. Contacts and attributes only when a manager exports (decision 83).
 */
export function landholderKml(
  parcels: readonly LandParcel[],
  colours: ReadonlyMap<string, PropertyColour>,
  roles: readonly TenantRole[],
): string {
  const withContacts = canExportLandholderContacts(roles);
  const colourOf = (p: LandParcel): PropertyColour => colours.get(p.id) ?? PROPERTY_PALETTE[0]!;
  const byFolder = new Map<string, LandParcel[]>();
  for (const p of parcels) {
    const f = p.folder || '';
    byFolder.set(f, [...(byFolder.get(f) ?? []), p]);
  }
  let kml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  kml += '<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>\n';
  kml += '  <name>Pinned Properties</name>\n';
  for (const p of byFolder.get('') ?? []) kml += placemarkKml(p, colourOf(p), withContacts, '  ');
  for (const [folder, items] of byFolder) {
    if (!folder) continue;
    kml += `  <Folder>\n    <name>${esc(folder)}</name>\n`;
    for (const p of items) kml += placemarkKml(p, colourOf(p), withContacts, '    ');
    kml += '  </Folder>\n';
  }
  return `${kml}</Document>\n</kml>`;
}

/** The folders offered by "Export boundaries…" (am:26752): each with its count, "(No folder)" first (the tool's "(Unfiled)"). */
export function exportFolderChoices(
  parcels: readonly LandParcel[],
): ReadonlyArray<{ readonly folder: string; readonly label: string; readonly count: number }> {
  const folders = [...new Set(parcels.map((p) => p.folder || ''))].sort((a, b) =>
    !a ? -1 : !b ? 1 : a.localeCompare(b, 'en-AU', { sensitivity: 'base' }),
  );
  return folders.map((f) => ({
    folder: f,
    label: f || '(No folder)',
    count: parcels.filter((p) => (p.folder || '') === f).length,
  }));
}

/** The words after an export. */
export function exportedWords(name: string, n: number, withContacts: boolean): string {
  return `Exported ${n} landholder ${n === 1 ? 'boundary' : 'boundaries'} to ${name}${withContacts ? ', with contacts' : ''}.`;
}

/** Each shape once: the parcels of `fresh` whose shape no parcel of `existing` has (am:24527). */
export function newShapes(fresh: readonly LandParcel[], existing: readonly LandParcel[]): LandParcel[] {
  const seen = new Set(existing.map((p) => geometryKey(p.geometry)));
  return fresh.filter((p) => {
    const k = geometryKey(p.geometry);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
